// frontend/app/components/lessonPlans/teachsense/useTeachSense.js
//
// Loads GET /api/lesson-plans/{id}/teachsense, lazily.
//
// `enabled` is false until the teacher actually opens the TeachSense tab, so a
// teacher who only ever reads class insights never pays for the call — this is
// the heaviest read in the lesson domain, joining mastery, responses and the
// exploration event stream.
//
// The panel that owns this is otherwise props-only. It keeps the fetch here
// rather than in the workspace because the tab is the only thing that knows
// when the data is wanted, and firing it on mount would defeat the point.

import { useCallback, useEffect, useState } from "react";

import { apiGetLessonTeachSense } from "../../../lessonPlans";

const IDLE = { data: null, loading: false, error: null };

export const useTeachSense = ({
  fetchWithAuth = null,
  lessonPlanId = null,
  classId = null,
  enabled = false,
} = {}) => {
  const [state, setState] = useState(IDLE);
  // Bumped by reload(); the effect keys off it so a retry re-runs the same
  // request without any of its other inputs having to change.
  const [attempt, setAttempt] = useState(0);

  const canLoad = Boolean(enabled && typeof fetchWithAuth === "function" && lessonPlanId);

  useEffect(() => {
    if (!canLoad) return undefined;

    let cancelled = false;
    setState({ data: null, loading: true, error: null });

    apiGetLessonTeachSense(fetchWithAuth, lessonPlanId, { classId })
      .then((data) => {
        if (!cancelled) setState({ data: data || null, loading: false, error: null });
      })
      .catch((err) => {
        // The message is the panel's to show; the shape only has to be truthy.
        if (!cancelled) {
          setState({ data: null, loading: false, error: err?.message || "TeachSense failed" });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canLoad, fetchWithAuth, lessonPlanId, classId, attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  return { ...state, reload };
};

export default useTeachSense;
